import { useQuery } from '@tanstack/vue-query'
import { apiClient } from '@/apis/instances/api-client'
import { checkApiResponse } from '@/apis/instances/check-api-response'
import { ColumnControllerFindParams } from '@/apis/swaggers/v3/api/reservation/reservation-swagger'
import { QUERY_KEY } from '@/apis/swaggers/swagger-docs'
import { MaybeGetter, QueryComposableParams, QueryOptions } from '@/types/query-options'
import { resolveMaybeGetter } from '@/util/resolve-maybe-getter'

type QueryParams = QueryComposableParams<ColumnControllerFindParams, unknown>

const fetchReservationColumns = async (params: ColumnControllerFindParams) => {
  const { data } = await apiClient.v3.columnControllerFind(params);
  return checkApiResponse(data);
}

export const reservationColumnKeys = {
  all: [QUERY_KEY.columnControllerFind] as const,
  list: (params: ColumnControllerFindParams) => [...reservationColumnKeys.all, params] as const,
}

/**
 * 명령형(await) 호출용 쿼리 정의 — `queryClient.fetchQuery(reservationColumnFindQueryOptions(params))`
 * 컬럼은 v2 컬럼 mutation·FCM(d_v2ReservationColumn*_el) invalidation 이 완전 커버 — staleTime Infinity.
 */
export const reservationColumnFindQueryOptions = (params: ColumnControllerFindParams) => ({
  queryKey: reservationColumnKeys.list(params),
  queryFn: () => fetchReservationColumns(params),
  staleTime: Infinity,
  gcTime: Infinity,
})

export const useReservationColumnFindQuery = ({ params, options }: QueryParams) => {
  return useQuery(() => ({
    queryKey: reservationColumnKeys.list(resolveMaybeGetter(params)),
    queryFn: () => fetchReservationColumns(resolveMaybeGetter(params)),
    ...resolveMaybeGetter(options ?? {}),
  }))
}

/**
 * 병원 컬럼 전체 목록의 선언형 구독 훅 — 표준 파라미터·staleTime 은 factory 가 관리하고,
 * enabled 등 컴포넌트별 옵션은 사용처(options)에서 제어한다.
 */
export const useReservationColumnListQuery = ({ hospitalID, options }: {
  hospitalID: MaybeGetter<string | undefined>
  options?: QueryOptions
}) => {
  return useQuery(() => {
    const id = resolveMaybeGetter(hospitalID)
    return {
      // swagger 생성 타입이 isRemoved 를 string 으로 잘못 문서화하고 있어 캐스팅 (서버는 boolean 을 JSON.parse)
      ...reservationColumnFindQueryOptions({ hospitalID: id, isRemoved: false } as unknown as ColumnControllerFindParams),
      enabled: !!id,
      ...resolveMaybeGetter(options ?? {}),
    }
  })
}
